import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, updateData, getcountries } from '../../../services/management/refundStatus/refundServices';
import Swal from 'sweetalert2';
import { Modal, Button, Form, Table } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../../components/Layout/Header';
import Footer from '../../../components/Layout/Footer';
import Sidebar from '../../../components/Layout/Sidebar';
import Spinner from '../../../components/Spinner';

// ── Identify current management user ─────────────────────────────────────────
const currentUserName = `admin`;

// ── Parse remarks stored as JSON string or array ──────────────────────────────
const parseRemarksHistory = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch (_) {}
  return [];
};

// ── Format ISO → "22 Jun 2026, 10:30 AM" ─────────────────────────────────────
const formatTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const RefundPending = () => {
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState({
    id: '', pr_id: '', series: '', sub_domain: '', name: '', email: '',
    countries: '', phone_no: '', currency: '', actual_amount: '',
    refund_amount: '', remaining_amount: '', paypal_email: '', refund_through: '',
    bank_name: '', bank_address: '', bank_country: '', postal_code: '',
    account_number: '', swift_code_ifsc_code: '', iban_code: '',
    refund_date: '', transaction_id: '', status: 0, updatedAt: '',
  });
  const [modalType, setModalType]     = useState('');
  const [showModal, setShowModal]     = useState(false);
  const [searchText, setSearchText]   = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading]         = useState(true);
  const [dropdownData, setDropdownData] = useState({ countries: [] });

  // ── Remarks state ─────────────────────────────────────────────────────────
  const [remarks,        setRemarks]        = useState('');
  const [remarksHistory, setRemarksHistory] = useState([]);
  const [remarksError,   setRemarksError]   = useState('');

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      const data = await getData({ status: 0 });
      setDataList(data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const [country] = await Promise.all([getcountries()]);
      setDropdownData({ countries: country });
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  }, []);

  useEffect(() => {
    const fetchDataAsync = async () => {
      setLoading(true);
      await fetchData();
      await fetchDropdownData();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setLoading(false);
    };
    fetchDataAsync();
  }, [fetchData, fetchDropdownData]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.refund_date || !form.transaction_id) {
      alert('Refund Date and Transaction ID are required!');
      return;
    }

    // Remarks mandatory only when history exists
    if (remarksHistory.length > 0 && !remarks.trim()) {
      setRemarksError('Remarks is required');
      return;
    }

    try {
      let updatedRemarks = [...remarksHistory];
      if (remarks.trim()) {
        updatedRemarks = [
          ...remarksHistory,
          { remark: remarks.trim(), timestamp: new Date().toISOString(), user: currentUserName },
        ];
      }

      if (modalType === 'edit') {
        await updateData({ ...form, status: 1, remarks: JSON.stringify(updatedRemarks) });
      }

      fetchData();
      setShowModal(false);
      Swal.fire({
        icon: 'success',
        title: 'Approved',
        text: 'Refund approved successfully!',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error during form submission:', error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Something went wrong!' });
    }
  };

  const openModal = (type, data = { id: '', refund_date: '', transaction_id: '' }) => {
    setModalType(type);
    setForm(data);

    // ── Parse and reset remarks ───────────────────────────────────────────
    setRemarksHistory(parseRemarksHistory(data.remarks));
    setRemarks('');
    setRemarksError('');

    setShowModal(true);
  };

  const resetModal = () => {
    setShowModal(false);
    setRemarks('');
    setRemarksHistory([]);
    setRemarksError('');
  };

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange   = (page) => setCurrentPage(page);

  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode?.toUpperCase()) {
      case 'USD': return '$'; case 'EUR': return '€'; case 'INR': return '₹';
      case 'GBP': return '£'; case 'RUB': return '₽'; case 'THB': return '฿';
      default: return currencyCode || '';
    }
  };

  const getCountryName = (code) => {
    const country = dropdownData.countries.find(c => c.code === code);
    return country ? country.name : code;
  };

  const filteredData = useMemo(() =>
    dataList
      .filter((data) =>
        data.name.toLowerCase().includes(searchText.toLowerCase()) ||
        data.sub_domain.toLowerCase().includes(searchText.toLowerCase()) ||
        data.actual_amount.toString().includes(searchText.toLowerCase()) ||
        data.refund_amount.toString().includes(searchText.toLowerCase())
      )
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
  [dataList, searchText]);

  const columns = [
    { name: '#', selector: (row, index) => (currentPage - 1) * 10 + (index + 1), sortable: false, width: '80px' },
    { name: 'Series',     selector: (row) => row.series,              sortable: true },
    { name: 'Sub Domain', selector: (row) => row.sub_domain || 'N/A', sortable: true },
    { name: 'Name',       selector: (row) => row.name || 'N/A',       sortable: true },
    { name: 'Country',    selector: (row) => getCountryName(row.countries || 'N/A'), sortable: true },
    {
      name: 'Actual Amount',
      selector: (row) => <b className="fs-4">{getCurrencySymbol(row.currency)} {row.actual_amount || 'N/A'}</b>,
      sortable: true,
    },
    {
      name: 'Refund Amount',
      selector: (row) => <b className="fs-4">{getCurrencySymbol(row.currency)} {row.refund_amount || 'N/A'}</b>,
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <Button className="me-2" variant="warning" size="sm" onClick={() => openModal('edit', row)}>Edit</Button>
      ),
    },
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '300px' }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className="fs-4">Revenue Management / Refund status / Pending Refund Details</p>
                </div>
              </div>

              <DataTable
                columns={columns} data={filteredData} pagination highlightOnHover responsive striped
                paginationPerPage={10} paginationComponentOptions={{ noRowsPerPage: true }}
                onChangePage={handlePageChange} subHeader
                subHeaderComponent={
                  <input className="form-control" type="text" placeholder="Search"
                    value={searchText} onChange={handleSearchChange}
                  />
                }
              />

              {/* ── Modal ── */}
              <Modal className="modal-lg" backdrop="static" show={showModal} onHide={resetModal}>
                <Modal.Header closeButton>
                  <Modal.Title>{modalType === 'add' ? 'Add Refund' : 'Edit Refund'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Table striped bordered hover responsive className="table-sm">
                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>Pending Refund Details</th></tr>
                    </thead>
                    <tbody />
                    <thead>
                      <tr><th className="bg-secondary text-white" colSpan={2}>User Details</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Name</td>             <td>{form.name}</td></tr>
                      <tr><td>Payment ID</td>       <td>{form.pr_id}</td></tr>
                      <tr><td>Email</td>            <td>{form.email}</td></tr>
                      <tr><td>Phone No</td>         <td>{form.phone_no}</td></tr>
                      <tr><td>Countries</td>        <td>{getCountryName(form.countries)}</td></tr>
                      <tr><td>Currency</td>         <td>{form.currency}</td></tr>
                      <tr><td>Actual Amount</td>    <td>{form.actual_amount}</td></tr>
                      <tr><td>Refund Amount</td>    <td>{form.refund_amount}</td></tr>
                      <tr><td>Remaining Amount</td> <td>{form.remaining_amount}</td></tr>
                      <tr><td>Series</td>           <td>{form.series}</td></tr>
                      <tr><td>Subdomain</td>        <td>{form.sub_domain}</td></tr>
                      <tr><td>Refund Through</td>   <td>{form.refund_through}</td></tr>
                    </tbody>

                    {form.paypal_email && (
                      <>
                        <thead><tr><th className="bg-secondary text-white" colSpan={2}>PayPal Details</th></tr></thead>
                        <tbody><tr><td>PayPal Email/ID</td><td>{form.paypal_email}</td></tr></tbody>
                      </>
                    )}

                    {(form.bank_name || form.bank_address || form.bank_country || form.postal_code ||
                      form.account_number || form.swift_code_ifsc_code || form.iban_code) && (
                      <>
                        <thead><tr><th className="bg-secondary text-white" colSpan={2}>Bank Details</th></tr></thead>
                        <tbody>
                          {form.bank_name            && <tr><td>Bank Name</td>       <td>{form.bank_name}</td></tr>}
                          {form.bank_address         && <tr><td>Bank Address</td>    <td>{form.bank_address}</td></tr>}
                          {form.bank_country         && <tr><td>Bank Country</td>    <td>{getCountryName(form.bank_country)}</td></tr>}
                          {form.postal_code          && <tr><td>Postal Code</td>     <td>{form.postal_code}</td></tr>}
                          {form.account_number       && <tr><td>Account Number</td>  <td>{form.account_number}</td></tr>}
                          {form.swift_code_ifsc_code && <tr><td>Swift/IFSC Code</td> <td>{form.swift_code_ifsc_code}</td></tr>}
                          {form.iban_code            && <tr><td>IBAN Code</td>       <td>{form.iban_code}</td></tr>}
                        </tbody>
                      </>
                    )}
                  </Table>

                  <Form>
                    <Form.Group className="mb-3">
                      <Form.Label>Refund Date <small className="text-danger">*</small></Form.Label>
                      <Form.Control type="date" name="refund_date" value={form.refund_date} onChange={handleChange} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Transaction ID <small className="text-danger">*</small></Form.Label>
                      <Form.Control type="text" name="transaction_id" value={form.transaction_id} onChange={handleChange} required />
                    </Form.Group>
                  </Form>

                  {/* ── Remarks section — only shown when history exists ── */}
                  {remarksHistory.length > 0 && (
                    <>
                      <div className="col-md-12 mb-3">
                        <Form.Label className="fs-5 text-dark fw-semibold">
                          Remarks History
                          <span className="badge bg-secondary ms-2" style={{ fontSize: '0.72rem' }}>
                            {remarksHistory.length}
                          </span>
                        </Form.Label>
                        <div className="border rounded" style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                          {[...remarksHistory].reverse().map((entry, idx) => (
                            <div key={idx} className="px-3 py-2 border-bottom" style={{ borderLeft: '3px solid #0d6efd' }}>
                              <div className="d-flex justify-content-between align-items-baseline">
                                <small className="text-muted d-block mb-1">🕒 {formatTimestamp(entry.timestamp)}</small>
                                {entry.user && (
                                  <span className="badge bg-info text-dark" style={{ fontSize: '0.72rem' }}>{entry.user}</span>
                                )}
                              </div>
                              <p className="mb-0 text-dark" style={{ fontSize: '0.9rem' }}>{entry.remark}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Form.Group className="col-md-12 mb-3">
                        <Form.Label className="fs-5 text-dark">
                          Remarks<span className="text-danger fs-4"> * </span>
                        </Form.Label>
                        <Form.Control
                          as="textarea" rows={2} value={remarks}
                          placeholder="Enter reason for this action…"
                          onChange={(e) => { setRemarks(e.target.value); setRemarksError(''); }}
                          isInvalid={!!remarksError}
                        />
                        <Form.Control.Feedback type="invalid">{remarksError}</Form.Control.Feedback>
                      </Form.Group>
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={resetModal}>Cancel</Button>
                  <Button variant="primary" onClick={handleSubmit}>{modalType === 'add' ? 'Add' : 'Save'}</Button>
                </Modal.Footer>
              </Modal>
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default RefundPending;
